import { ThemeProvider } from 'styled-components';

import { lightTheme } from 'theme';

import { PersonaId } from '../../personas';
import * as styled from '../styles/story-section';
import { EarningsCalculatorCard } from './earnings-calculator';
import { FakePhoneReviews } from './fake-phone-reviews';
import { LoopVisual } from './loop-visual';
import { MarketplaceFrame } from './marketplace-frame';
import { ProviderMomentum } from './provider-momentum';
import { SocialBios } from './social-bios';

type Props = {
  personaId: PersonaId;
  onWaitlistClick: () => void;
};

export const StorySection = ({ personaId, onWaitlistClick }: Props) => (
  <styled.StorySection>
    <styled.StoryHeader>
      <styled.SectionHeading>
        How Kudable turns your followers — and people searching — into
        bookings
      </styled.SectionHeading>
      <styled.SectionSub>Three steps. One loop.</styled.SectionSub>
    </styled.StoryHeader>

    <styled.PanelHeader>
      <styled.StoryActLabel>Step 1 · Get discovered</styled.StoryActLabel>
      <styled.StoryActTitle>
        Show up where clients are looking
      </styled.StoryActTitle>
    </styled.PanelHeader>
    <styled.StoryPanel>
      <styled.StoryVisual>
        <ThemeProvider theme={lightTheme}>
          <SocialBios personaId={personaId} />
        </ThemeProvider>
      </styled.StoryVisual>
      <styled.StoryText>
        <styled.StoryActDesc>
          One link in your Instagram bio, WhatsApp status, or business card —
          and{' '}
          <styled.StoryActDescAccent>
            every follower, social media post, or view becomes a potential
            booking
          </styled.StoryActDescAccent>
          .
        </styled.StoryActDesc>
      </styled.StoryText>
    </styled.StoryPanel>

    <styled.StoryPanel $reverse>
      <styled.StoryVisual $hasHint>
        <ThemeProvider theme={lightTheme}>
          <MarketplaceFrame />
        </ThemeProvider>
        <styled.ScrollHint>
          <styled.ScrollHintTitle>
            <span aria-hidden>👀</span> This is Kudable search
          </styled.ScrollHintTitle>
          <styled.ScrollHintSub>
            Clients search — your profile shows up.
          </styled.ScrollHintSub>
        </styled.ScrollHint>
      </styled.StoryVisual>
      <styled.StoryText>
        <styled.StoryActDesc>
          No post, no share needed — when someone searches Kudable for what you
          do,{' '}
          <styled.StoryActDescAccent>
            your profile is right there in the results
          </styled.StoryActDescAccent>
          , ranked by your reviews.
        </styled.StoryActDesc>
      </styled.StoryText>
    </styled.StoryPanel>

    <styled.PanelHeader>
      <styled.StoryActLabel>Step 2 · Collect reviews</styled.StoryActLabel>
      <styled.StoryActTitle>
        Clients choose you with confidence
      </styled.StoryActTitle>
    </styled.PanelHeader>
    <styled.StoryPanel $reverse>
      <styled.StoryVisual $hasHint>
        <ThemeProvider theme={lightTheme}>
          <styled.StoryPhoneFrame>
            <FakePhoneReviews personaId={personaId} />
          </styled.StoryPhoneFrame>
        </ThemeProvider>
        <styled.StoryAnnotation $top="11rem" $right="-1rem">
          🛡 Verified reviews
        </styled.StoryAnnotation>
        <styled.StoryAnnotation $top="24rem" $right="-1rem">
          🎥 Photo &amp; video reviews
        </styled.StoryAnnotation>
        <styled.StoryAnnotation $top="18rem" $left="-1rem">
          🖼 Portfolio
        </styled.StoryAnnotation>
        <styled.StoryAnnotationAccent $bottom="5rem" $left="-1rem">
          ✓ Booked
        </styled.StoryAnnotationAccent>
        <styled.ScrollHint>
          <styled.ScrollHintTitle>
            <span aria-hidden>👀</span> This is what your client sees
          </styled.ScrollHintTitle>
          <styled.ScrollHintSub>
            Scroll the phone — they book in one tap.
          </styled.ScrollHintSub>
        </styled.ScrollHint>
      </styled.StoryVisual>
      <styled.StoryText>
        <styled.StoryActDesc>
          They land on your link and scroll the verified reviews — clients
          review you with{' '}
          <styled.StoryActDescAccent>
            videos, photos, and even voice notes
          </styled.StoryActDescAccent>{' '}
          that boost your profile — then book a slot without ever picking up
          the phone. No haggling, no DMs, no back-and-forth.
        </styled.StoryActDesc>
        <EarningsCalculatorCard
          personaId={personaId}
          onWaitlistClick={onWaitlistClick}
        />
      </styled.StoryText>
    </styled.StoryPanel>

    <styled.PanelHeader>
      <styled.StoryActLabel>Step 3 · Receive bookings</styled.StoryActLabel>
      <styled.StoryActTitle>
        From quiet weeks to fully booked
      </styled.StoryActTitle>
    </styled.PanelHeader>
    <styled.StoryPanel>
      <styled.StoryVisual>
        <ProviderMomentum personaId={personaId} />
      </styled.StoryVisual>
      <styled.StoryText>
        <styled.StoryActDesc>
          Clients book in-app and confirm the job when it's done. Every
          completed booking earns a verified review, your reputation score
          climbs, and the next client books faster than the last — the flywheel
          that fills your calendar.
        </styled.StoryActDesc>
      </styled.StoryText>
    </styled.StoryPanel>

    <LoopVisual personaId={personaId} />
  </styled.StorySection>
);
